import {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import { useSearchParams } from "react-router-dom";
import isEqual from "lodash/isEqual";
import merge from "lodash/merge";
import cloneDeep from "lodash/cloneDeep";
import crypto from "@lib/services/crypto";
import { ApiParams } from "@lib/types/Api";

type Props = ApiParams & {
  isShowParamsInUrl?: boolean;
};

export const useParamsManager = (props: Props) => {
  const { isShowParamsInUrl = true, ...initialParams } = props;
  // console.log("🚀 ~ useParamsManager ~ initialParams:", initialParams);
  const [searchParams, setSearchParams] = useSearchParams();
  const [params, setParams] = useState<ApiParams>();
  const [paramsKey] = useState<string>("params");
  const [encryptedParams, setEncryptedParams] = useState<string>("");
  const paramsRef = useRef<ApiParams>();
  const isFirstRender = useRef(true);
  const initialParamsRef = useRef<ApiParams>(initialParams);

  const handleSetParams = useCallback(
    (newParams: ApiParams) => {
      if (!params || Object.keys(params).length == 0) return null;

      // For user-initiated changes, merge with current params only
      const obj = merge({}, params, newParams);

      // console.log("🚀 ~ user change - merging params:", {
      //   current: params,
      //   newParams,
      //   result: obj,
      // });

      // Handle filter merging for user changes
      if (newParams.filter) {
        const newKeys = newParams.filter.map((f) => f.split("||")[0]);

        obj.filter = [
          ...(obj.filter || []).filter(
            (f) => !newKeys.includes(f.split("||")[0]),
          ),
          ...newParams.filter,
        ];
      }

      setParams(obj);
    },
    [params],
  );

  const setFilter = useCallback(
    (newFilter: string) => {
      const { filter = [] } = params || {};
      const filterKey = newFilter.split("||")[0];
      const index = filter.findIndex((f) => f.startsWith(`${filterKey}||`));

      let updatedFilter;
      if (index !== -1) {
        updatedFilter = [...filter];
        updatedFilter[index] = newFilter;
      } else {
        updatedFilter = [...filter, newFilter];
      }

      handleSetParams({ filter: updatedFilter });
    },
    [params, handleSetParams],
  );

  const onPageChange = useCallback(
    (page: number, limit: number) => {
      if (page !== params.page || limit !== params.limit) {
        handleSetParams({ page, limit });
      }
    },
    [params?.page, params?.limit],
  );

  const handleRemoveFilters = useCallback(
    (keys: string[]) => {
      if (!params?.filter) return;

      const updatedFilters = params.filter.filter((filterItem) => {
        const key = filterItem.split("||")[0];
        return !keys.includes(key);
      });

      setParams({ ...params, filter: updatedFilters });
    },
    [params],
  );

  const makeHash = useCallback(
    (_params: ApiParams) => {
      if (!params || Object.keys(params).length == 0) return null;
      const encrypted = crypto.encrypt(_params);
      setEncryptedParams(encrypted);
      return encrypted;
    },
    [params],
  );

  useEffect(() => {
    if (isFirstRender.current) return;
    if (!encryptedParams) return;
    if (isEqual(params, initialParams)) return;

    if (isShowParamsInUrl) {
      setSearchParams({ params: encryptedParams }, { replace: false });
    }
  }, [encryptedParams]);

  useLayoutEffect(() => {
    if (params && Object.keys(params).length > 0) return;

    let obj: ApiParams = {};
    if (!params) {
      obj = { ...initialParams };
    }

    const _params = searchParams.get(paramsKey);

    if (isShowParamsInUrl) {
      if (_params) {
        try {
          const decrypted: any = crypto.decrypt(_params);
          if (!isEqual(decrypted, params) || Object.keys(params).length == 0) {
            obj = decrypted;
            paramsRef.current = decrypted;
          }
        } catch (error) {
          console.error("Decryption failed:", error);
        }
      }
    }

    setParams(obj);
  }, []);

  // Track initialParams changes and update state accordingly
  useEffect(() => {
    if (isFirstRender.current) return;

    // Check if initialParams have changed
    if (!isEqual(initialParams, initialParamsRef.current)) {
      // console.log("🚀 ~ initialParams changed:", {
      //   old: initialParamsRef.current,
      //   new: initialParams,
      // });

      initialParamsRef.current = cloneDeep(initialParams);

      // When initialParams change, completely replace params with initialParams
      // Don't merge, just use initialParams as the new base
      const updatedParams = { ...initialParams };

      // console.log("🚀 ~ initialParams change - replacing params:", {
      //   old: params,
      //   new: updatedParams,
      // });

      // Always update when initialParams change
      setParams(updatedParams);
    }
  }, [initialParams, params]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!params || Object.keys(params).length == 0) return null;

    // console.log("🚀 ~ useEffect ~ params:", params, paramsRef.current);
    if (!isEqual(params, paramsRef.current)) {
      // console.log("🚀 ~ useEffect ~ isEqual:");
      makeHash(params);
      paramsRef.current = params;
    }
  }, [params]);

  return {
    params,
    setParams: handleSetParams,
    handleRemoveFilters,
    paramsKey,
    encryptedParams,
    onPageChange,
    setFilter,
  };
};
